/**
 * Session lifecycle effects for AuthSession.
 *
 * Each exported function corresponds to a single session lifecycle operation.
 * Effects receive deps, perform I/O, and communicate results exclusively by
 * calling `dispatch`. No state is mutated here — all mutations happen in
 * `reducer.ts`.
 *
 * Account mutation effects (phone, password, delete, profile, email) live in
 * `account-actions.ts`.
 */

import { SessionStep } from '../../constants';
import { currentAbsoluteUrl } from './return-to';
import type { User } from '../../types/user';
import type { AuthSessionAction, AuthSessionInternalDeps } from './types';

type Dispatch = (action: AuthSessionAction) => void;

/**
 * Shared "auth succeeded" primitive used by session lifecycle and AccountActions.
 *
 * Persists the user and JWT to cookies, clears any pending cross-redirect
 * error or needs-phone flag, then dispatches `AUTH_COMPLETE`.
 */
export function authCompleteEffect(
  user: User,
  dispatch: Dispatch,
  deps: AuthSessionInternalDeps,
): void {
  deps.persistence.dispatch({ type: 'SAVE_USER', user });
  if (user.jwt) deps.persistence.dispatch({ type: 'SAVE_JWT', jwt: user.jwt });
  deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_ERROR' });
  deps.sessionStore.dispatch({ type: 'CLEAR_NEEDS_PHONE' });
  deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PROFILE' });
  deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_AUTH' });
  dispatch({ type: 'AUTH_COMPLETE', user });
}

/**
 * Runs once on startup to establish the initial session state.
 *
 * Three paths:
 * - **Pending auth present** — a social sign-in completed on the previous page
 *   and staged the user/JWT to localStorage instead of writing cookies (cookies
 *   don't reliably survive iOS Safari's post-OAuth navigation). Promote it to
 *   cookies now, on the destination page, where there's no navigation race.
 * - **SSR user present** — the server already authenticated the user; we call
 *   `refreshSession` to exchange the SSR token for a fresh client-side session.
 * - **No SSR user** — we load the persisted user from cookies and mark the
 *   session loaded immediately (no network call needed).
 */
export async function bootstrapEffect(
  ssrUser: User | null,
  dispatch: Dispatch,
  deps: AuthSessionInternalDeps,
): Promise<void> {
  const pending = deps.sessionStore.getPendingAuth();
  if (pending) {
    deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_AUTH' });
    authCompleteEffect(pending, dispatch, deps);
    return;
  }
  if (ssrUser) {
    await bootstrapWithSsrUserEffect(ssrUser, dispatch, deps);
    return;
  }
  const loaded = deps.persistence.loadUser();
  dispatch({ type: 'SESSION_LOADED', user: loaded });
}

/**
 * SSR bootstrap path: attempts to refresh the server-provided session.
 *
 * - On success: persists the refreshed user and dispatches `SESSION_LOADED`.
 * - On failure when the provider does not require a session guard (legacy):
 *   dispatches `SESSION_LOADED` with the SSR user so the app stays usable.
 * - On failure when the provider requires a session guard (Cognito): clears
 *   all persistence and dispatches `SESSION_CLEARED` to force a clean sign-in.
 */
async function bootstrapWithSsrUserEffect(
  ssrUser: User,
  dispatch: Dispatch,
  deps: AuthSessionInternalDeps,
): Promise<void> {
  try {
    const result = await deps.service.refreshSession();
    if (result?.user) {
      deps.persistence.dispatch({ type: 'SAVE_USER', user: result.user });
      if (result.user.jwt) deps.persistence.dispatch({ type: 'SAVE_JWT', jwt: result.user.jwt });
      dispatch({ type: 'SESSION_LOADED', user: result.user });
      return;
    }
  } catch (err) {
    deps.observability.dispatch({ type: 'bootstrap_failure', error: err });
    if (!deps.service.supportedFeatures.requiresSessionGuard) {
      dispatch({ type: 'SESSION_LOADED', user: ssrUser });
      return;
    }
  }
  deps.persistence.dispatch({ type: 'CLEAR' });
  deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_ERROR' });
  deps.sessionStore.dispatch({ type: 'CLEAR_NEEDS_PHONE' });
  deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PROFILE' });
  dispatch({ type: 'SESSION_CLEARED' });
}

/**
 * Replaces the in-memory user and persists without touching pending actions.
 * Called after profile updates that don't change auth state (e.g. phone added).
 */
export function userUpdatedEffect(
  user: User,
  dispatch: Dispatch,
  deps: AuthSessionInternalDeps,
): void {
  deps.persistence.dispatch({ type: 'SAVE_USER', user });
  if (user.jwt) deps.persistence.dispatch({ type: 'SAVE_JWT', jwt: user.jwt });
  dispatch({ type: 'USER_UPDATED', user });
}

/**
 * Signs the user out via the auth service, then clears all local state.
 *
 * The service call is best-effort — a network failure is logged and tracked
 * but does not prevent the local session from being cleared. This ensures the
 * user is always signed out locally even if the server is unreachable.
 */
export async function signOutEffect(
  redirectUrl: string | undefined,
  dispatch: Dispatch,
  deps: AuthSessionInternalDeps,
): Promise<void> {
  try {
    await deps.service.signOut(redirectUrl ?? currentAbsoluteUrl());
  } catch (err) {
    deps.observability.dispatch({ type: 'signout_failure', error: err });
    deps.logger.log(SessionStep.SignOutError, { error: err });
  }
  deps.persistence.dispatch({ type: 'CLEAR' });
  deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_ERROR' });
  deps.sessionStore.dispatch({ type: 'CLEAR_NEEDS_PHONE' });
  deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PROFILE' });
  dispatch({ type: 'SESSION_CLEARED' });
}

/**
 * Asks the auth service for a fresh session.
 *
 * Called on a 15-minute timer by `useSessionGuard` (Cognito users only).
 * On success the user and JWT cookie are updated; on failure the session is cleared.
 */
export async function refreshSessionEffect(
  dispatch: Dispatch,
  deps: AuthSessionInternalDeps,
): Promise<void> {
  try {
    const result = await deps.service.refreshSession();

    if (result && 'needsPhone' in result && result.needsPhone) {
      deps.observability.dispatch({ type: 'session_refresh_success' });
      return;
    }

    if (result?.user) {
      deps.observability.dispatch({ type: 'session_refresh_success' });
      authCompleteEffect(result.user, dispatch, deps);
      return;
    }

    deps.persistence.dispatch({ type: 'CLEAR' });
    dispatch({ type: 'SESSION_CLEARED' });
  } catch (err) {
    deps.observability.dispatch({ type: 'session_failure', error: err });
    deps.persistence.dispatch({ type: 'CLEAR' });
    dispatch({ type: 'SESSION_CLEARED' });
  }
}
